import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../components/Signup/Signup.css';
import { useSenders } from '../hooks/useApiHooks';
import { AddorEditSender } from '../components/SenderManagement/AddorEditSender';
import { DeleteSender } from '../components/SenderManagement/DeleteSender';
import { SendersTable } from '../components/SenderManagement/SendersTable';
import { ChevronDown, ArrowUpDown, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage, getSenderManagementTranslation } from '../hooks/useLanguage';
import { CardStat } from '../components/CardStat';

function SenderManagement() {
  const dispatch = useDispatch();
  const senders = useSelector((state) => state.senders || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSpanish] = useLanguage();

  useSenders();

  const [formData, setFormData] = useState({
    nombre: '',
    identificacion_fiscal: '',
    eori: '',
    direccion: '',
    localidad: '',
    pais: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentSenderId, setCurrentSenderId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [senderToDelete, setSenderToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { value: 'name-asc', label: getSenderManagementTranslation('nameAZ', isSpanish) },
    { value: 'name-desc', label: getSenderManagementTranslation('nameZA', isSpanish) },
    { value: 'date-desc', label: getSenderManagementTranslation('newestFirst', isSpanish) },
    { value: 'date-asc', label: getSenderManagementTranslation('oldestFirst', isSpanish) }
  ];

  const filteredSenders = senders.filter(sender => 
    sender.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sender.identificacion_fiscal?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sender.eori?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sender.pais?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedSenders = [...filteredSenders].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.nombre.localeCompare(b.nombre);
      case 'name-desc':
        return b.nombre.localeCompare(a.nombre);
      case 'date-asc':
        return new Date(a.date_created) - new Date(b.date_created);
      case 'date-desc':
        return new Date(b.date_created) - new Date(a.date_created);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find(option => option.value === sortBy)?.label || getSenderManagementTranslation('sortBy', isSpanish);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAdd = () => {
    setIsEditing(false);
    setFormData({
      nombre: '',
      identificacion_fiscal: '',
      eori: '',
      direccion: '',
      localidad: '',
      pais: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (sender) => {
    setIsEditing(true);
    setCurrentSenderId(sender.id);
    setFormData({
      nombre: sender.nombre || '',
      identificacion_fiscal: sender.identificacion_fiscal || '',
      eori: sender.eori || '',
      direccion: sender.direccion || '',
      localidad: sender.localidad || '',
      pais: sender.pais || '',
    });
    setIsModalOpen(true);
  };

  const openDeleteModal = (sender) => {
    setSenderToDelete(sender);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSenderToDelete(null);
  };

  const confirmDelete = async () => {
    if (!senderToDelete) return;

    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/items/senders/${senderToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete sender');
      }

      dispatch({ type: 'DELETE_SENDER', payload: senderToDelete.id });
      toast.success(getSenderManagementTranslation('successDeleteSender', isSpanish));
      closeDeleteModal();

    } catch (err) {
      console.error('Error deleting sender:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const apiData = {
      nombre: formData.nombre,
      identificacion_fiscal: formData.identificacion_fiscal,
      eori: formData.eori,
      direccion: formData.direccion,
      localidad: formData.localidad,
      pais: formData.pais,
    };

    try {
      let response;

      if (isEditing) {
        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/items/senders/${currentSenderId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData),
        });

        toast.success(getSenderManagementTranslation('successUpdateSender', isSpanish));
      } else {
        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/items/senders`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData),
        });
        toast.success(getSenderManagementTranslation('successCreateSender', isSpanish));
      }

      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} sender`);
      }

      const data = await response.json();

      const senderData = data.data || data;

      if (isEditing) {
        dispatch({ type: 'UPDATE_SENDER', payload: senderData });
      } else {
        dispatch({ type: 'ADD_SENDER', payload: senderData });
      }

      setIsModalOpen(false);
      setFormData({
        nombre: '',
        identificacion_fiscal: '',
        eori: '',
        direccion: '',
        localidad: '',
        pais: '',
      });

      fetchSenders();

    } catch (err) {
      console.error('Error submitting sender:', err);
      if (isEditing) {
        toast.error(getSenderManagementTranslation('errorUpdateSender', isSpanish));
      } else {
        toast.error(getSenderManagementTranslation('errorCreateSender', isSpanish));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSenders = async () => {
    const userId = sessionStorage.getItem('user_id');
    if (!userId) {
      console.warn('No user_id found in sessionStorage, sender fetch might fail');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/items/senders?filter%5Buser_id%5D%5B_eq%5D=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch senders');
      }

      const data = await response.json();

      // Check if data has a data property, if so use it, otherwise use data directly
      const sendersData = data.data || (Array.isArray(data) ? data : []);

      dispatch({ type: 'SET_SENDERS', payload: sendersData });
    } catch (err) {
      console.error("Error fetching senders:", err.message);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Find the city (localidad) with the most senders
  const cityCounts = senders.reduce((acc, sender) => {
    if (sender.localidad) {
      acc[sender.localidad] = (acc[sender.localidad] || 0) + 1;
    }
    return acc;
  }, {});
  let topCity = '-';
  let topCityCount = 0;
  for (const [city, count] of Object.entries(cityCounts)) {
    if (count > topCityCount) {
      topCity = city;
      topCityCount = count;
    }
  }

  // Find the country (pais) with the most senders
  const countryCounts = senders.reduce((acc, sender) => {
    if (sender.pais) {
      acc[sender.pais] = (acc[sender.pais] || 0) + 1;
    }
    return acc;
  }, {});
  let topCountry = '-';
  let topCountryCount = 0;
  for (const [country, count] of Object.entries(countryCounts)) {
    if (count > topCountryCount) {
      topCountry = country;
      topCountryCount = count;
    }
  }

  return (
    <div className="text-left mt-28">
      <h1 className='page-header text-3xl font-bold mb-20'>{getSenderManagementTranslation('senderInformationManagement', isSpanish)}</h1>
      <div
  className="grid grid-cols-[1fr_3fr_1fr] gap-4 mb-8 pb-8 border-bottom-gray"
>
  <CardStat value={senders.length} title={isSpanish ? 'Remitentes' : 'Senders'} />
  <CardStat value={topCity} title={isSpanish ? 'Ciudad con más remitentes' : 'Top Sender City'} />
  <CardStat value={topCountry} title={isSpanish ? 'País con más remitentes' : 'Top Sender Country'} />
</div>
      <div className='flex justify-between gap-4 mb-4 items-center'>
        <h2 className='text-xl font-bold'>{isSpanish ? 'Lista de remitentes' : 'Sender List'}</h2>
        <div className="flex gap-2 items-center">
           {/* Custom Sort Dropdown */}
           <div className="relative button-no-hover-effect">
            <button
              className="flex items-center gap-2 px-2 py-2 rounded-full truncate"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-64 pl-10 pr-4 py-2 border-container rounded-full"
              placeholder={getSenderManagementTranslation('searchSenders', isSpanish)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={handleAdd}
            className='border rounded-full py-2 px-4 pr-6 truncate flex gap-2 items-center bg-default text-white'
            disabled={isLoading}
          >
            <Plus className="size-5 stroke-white" />
            {getSenderManagementTranslation('addSender', isSpanish)}
          </button>
        </div>
      </div>

      {/* Modal for Adding/Editing Sender */}
      {isModalOpen && (
        <AddorEditSender handleSubmit={handleSubmit} handleChange={handleChange} isEditing={isEditing} closeModal={closeModal}
          formData={formData} isLoading={isLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && senderToDelete && (
        <DeleteSender closeDeleteModal={closeDeleteModal} isLoading={isLoading} senderToDelete={senderToDelete} confirmDelete={confirmDelete} />
      )}

      <SendersTable senders={sortedSenders} isLoading={isLoading} handleEdit={handleEdit} openDeleteModal={openDeleteModal} />

    </div>
  );
}

export default SenderManagement;